import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Send, RefreshCw, Sparkles, Clock, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PreMarketBriefTab = () => {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');

  const loadBrief = async (force = false) => {
    setError('');
    if (force) setRegenerating(true); else setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/premarket-brief${force ? '?force=true' : ''}`, {
        cache: 'no-store',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setBrief(data);
    } catch (e) {
      setError(`Failed to load brief: ${e.message}`);
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/premarket-brief/history?limit=10`);
      const d = await r.json();
      setHistory(d.briefs || []);
    } catch { /* ignore */ }
  };

  const broadcastNow = async (regenerate = false) => {
    if (!window.confirm(
      `Send the brief to ALL Telegram channels (Complete Package, Options Only, Equity Only) right now?\n\n` +
      `Subscribers will receive a notification. This is${regenerate ? '' : ' NOT'} a regenerate-first call.`
    )) return;
    setBroadcasting(true);
    setError('');
    try {
      const r = await fetch(
        `${API_URL}/api/admin/premarket-brief/broadcast${regenerate ? '?regenerate=true' : ''}`,
        { method: 'POST', cache: 'no-store' }
      );
      const data = await r.json();
      setBrief(prev => ({ ...(prev || {}), ...data }));
      loadHistory();
      const okCount = (data.broadcast_results || []).filter(x => x.ok).length;
      const total = (data.broadcast_results || []).length;
      alert(`Broadcast complete — ${okCount}/${total} channels delivered.`);
    } catch (e) {
      setError(`Broadcast failed: ${e.message}`);
    } finally {
      setBroadcasting(false);
    }
  };

  useEffect(() => {
    loadBrief();
    loadHistory();
  }, []);

  return (
    <div className="space-y-6" data-testid="premarket-brief-tab">
      {/* Header */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Clock className="w-5 h-5 text-amber-600" />
                Daily Pre-Market Brief
              </CardTitle>
              <CardDescription>
                Auto-generated 4-line summary · Auto-broadcast to all Telegram channels at <strong>7:45 AM IST</strong> Mon-Fri
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => loadBrief(true)}
                disabled={regenerating || broadcasting}
                size="sm"
                variant="outline"
                data-testid="brief-regenerate-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? 'Regenerating…' : 'Regenerate'}
              </Button>
              <Button
                onClick={() => broadcastNow(false)}
                disabled={broadcasting || !brief?.text}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="brief-broadcast-btn"
              >
                <Send className={`w-4 h-4 mr-1 ${broadcasting ? 'animate-pulse' : ''}`} />
                {broadcasting ? 'Sending…' : 'Send to Telegram Now'}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm">{error}</div>
      )}

      {/* Today's brief preview */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4 text-purple-600" />
            Today's Brief · {brief?.date || '—'}
          </CardTitle>
          <CardDescription className="text-xs">
            This is exactly what subscribers will see in Telegram
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-gray-500">
              <Sparkles className="w-6 h-6 mx-auto animate-pulse mb-2" />
              Generating brief…
            </div>
          ) : !brief?.text ? (
            <p className="text-sm text-gray-500">No brief available yet.</p>
          ) : (
            <div className="bg-gradient-to-br from-zinc-900 to-black rounded-lg p-5 text-white font-mono text-sm leading-relaxed border border-amber-500/20" data-testid="brief-preview">
              <div className="text-amber-400 font-bold mb-2">
                📈 Pre-Market Brief · {brief.date}
              </div>
              <pre className="whitespace-pre-wrap font-sans">{brief.text}</pre>
              <div className="mt-3 pt-3 border-t border-zinc-800 text-xs text-zinc-500 italic">
                uppercircuitstocks.com · AI-curated by Upper Circuit Stocks
              </div>
            </div>
          )}

          {brief?.broadcasted_at && (
            <div className="mt-3 flex items-center gap-2 text-xs text-green-700">
              <CheckCircle2 className="w-4 h-4" />
              Broadcast at {new Date(brief.broadcasted_at).toLocaleString('en-IN')}
              {brief.broadcast_results && (
                <span className="ml-2">
                  · {brief.broadcast_results.filter(r => r.ok).length}/{brief.broadcast_results.length} channels OK
                </span>
              )}
            </div>
          )}

          {brief?.broadcast_results && (
            <div className="mt-3 flex flex-wrap gap-2">
              {brief.broadcast_results.map((r, i) => (
                <Badge
                  key={i}
                  variant={r.ok ? 'default' : 'destructive'}
                  className={r.ok ? 'bg-green-100 text-green-800 border-green-300' : ''}
                >
                  {r.ok ? <CheckCircle2 className="w-3 h-3 mr-1 inline" /> : <XCircle className="w-3 h-3 mr-1 inline" />}
                  {r.channel}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Recent Briefs · Last 10 days</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-gray-500">No history yet.</p>
          ) : (
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={i} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-sm">{h.date}</span>
                    {h.broadcasted_at ? (
                      <Badge className="bg-green-100 text-green-800 text-[10px]">
                        Sent · {h.broadcast_results?.filter(r => r.ok).length || 0}/{h.broadcast_results?.length || 0}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Not broadcast</Badge>
                    )}
                  </div>
                  <pre className="whitespace-pre-wrap text-xs text-gray-700 font-sans line-clamp-3">
                    {h.text}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PreMarketBriefTab;
